import React, { useCallback, useContext } from 'react';
import { View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import { Button, CardView } from 'design-system';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import Squircle from 'design-system/Squircle';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import IconEmptyState from 'design-system/icons/IconEmptyState';
import { IconArrowBack, IconSearch } from 'design-system/icons';
import useColors from 'hooks/useColors';
import { AddToWatchListFlow } from 'utils/types/navigation';
import { IconChecklist } from 'design-system/icons/IconChecklist';
import { Amplitude } from 'utils';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppSelector } from 'store/hooks';
import { InvestWalkthroughContext } from 'features/feed/components/walkthrough/context';
import { selectTradingAccount } from '../reducers/selectors';
const strings = {
    title: 'Nothing to show...',
    titleWatchlist: 'Nothing to show',
    subtitleActivity: 'Top up your account and make a transaction to see activity.',
    subtitleOrders: 'You have no queued orders',
    subtitleLimitAndStopOrders: 'You have no queued orders',
    subtitlePendingOrders: 'You have no pending orders',
    subtitleWatchlist: 'Stay up to date by adding some companies to this list.',
    noPositions: 'Your investments will show up here. You can now start looking for your next great trade.',
    noPositionsSeccl: 'Find and explore funds to start building your portfolio.',
    searchStocks: 'Search stocks',
    searchStocksSeccl: 'Search funds',
    searchForStocks: 'Search for stocks',
    createWatchlistTitle: 'Create a list',
    createWatchlistDesc: 'Track your favourite stocks for your next great trade',
    recurringOrderTitle: 'Add a recurring order',
    recurringOrderDesc: 'Set an interval and amount to invest on autopilot',
};
const getSubtitle = (props) => {
    if ('activity' in props) {
        return props.subtitle ?? strings.subtitleActivity;
    }
    if ('pendingOrders' in props && props.pendingOrders) {
        return strings.subtitlePendingOrders;
    }
    if ('limitAndStopOrders' in props && props.limitAndStopOrders) {
        return strings.subtitleLimitAndStopOrders;
    }
    return strings.subtitleOrders;
};
const EmptyState = (props) => {
    const navigation = useMainStackNavigation();
    const styles = useStyles(styleSet);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const isInInvestWalkthrough = useContext(InvestWalkthroughContext);
    const colors = useColors();
    const navigateToDiscover = useCallback(() => {
        Amplitude.logEvent('Open Discovery', {
            source: 'portfolio_stocks_list',
        });
        navigation.navigateDeprecated('InvestDiscover');
    }, []);
    const onCreateWatchListBtnPress = useCallback(() => {
        navigation.navigateDeprecated('InvestCreateWatchList', {
            createParams: {
                flow: AddToWatchListFlow.AddToWatchListFromInvest,
            },
        });
    }, []);
    const handleRecurringBuyFlow = useCallback(() => {
        navigation.navigateDeprecated('InvestSearchStock', { recurringOrderFlow: true });
    }, []);
    if ('recurringOrders' in props) {
        return (<TouchableBounce onPress={handleRecurringBuyFlow}>
        <CardView withPadding noMarginTop noMarginBottom>
          <View style={styles.row}>
            <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
              <IconArrowBack alt gradientKey="ultimate"/>
            </Squircle>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text Text-16>{strings.recurringOrderTitle}</Text>
              <Text TextThin-14 Gray>
                {strings.recurringOrderDesc}
              </Text>
            </View>
          </View>
        </CardView>
      </TouchableBounce>);
    }
    if ('watchlistPortfolio' in props) {
        return (<TouchableBounce onPress={onCreateWatchListBtnPress}>
        <CardView withPadding noMarginTop noMarginBottom>
          <View style={styles.row}>
            <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
              <IconChecklist />
            </Squircle>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text Text-16>{strings.createWatchlistTitle}</Text>
              <Text TextThin-14 Gray>
                {strings.createWatchlistDesc}
              </Text>
            </View>
          </View>
        </CardView>
      </TouchableBounce>);
    }
    if ('positions' in props) {
        return (<TouchableBounce onPress={navigateToDiscover}>
        <CardView withPadding noMarginTop noMarginBottom style={isInInvestWalkthrough ? styles.walkthroughCardStyles : null}>
          <View style={styles.row}>
            <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
              <IconSearch alt gradientKey="ultimate"/>
            </Squircle>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text Text-16>{tradingAccount?.isAlpaca ? strings.searchStocks : strings.searchStocksSeccl}</Text>
              <Text TextThin-14 Gray>
                {tradingAccount?.isAlpaca ? strings.noPositions : strings.noPositionsSeccl}
              </Text>
            </View>
          </View>
        </CardView>
      </TouchableBounce>);
    }
    if ('watchlist' in props) {
        return (<CardView noMarginBottom noMarginTop>
        <View style={styles.containerWithRoundedCorners}>
          <IconEmptyState />
          <Spacer.H16 />
          <Text Text-16 centered>
            {strings.titleWatchlist}
          </Text>
          <Spacer h={4}/>
          <Text TextThin-14 Secondary centered style={styles.extraHorizontalPadding}>
            {strings.subtitleWatchlist}
          </Text>
          <Spacer.H16 />
          <Button grey small hug title={strings.searchForStocks} onPress={props.onPress}/>
        </View>
      </CardView>);
    }
    return (<CardView noMarginBottom>
      <View style={styles.container}>
        <IconEmptyState />
        <Spacer h={26}/>
        <View style={styles.textContainer}>
          <Text Text-20 centered>
            {strings.title}
          </Text>
          <Spacer h={4}/>
          <Text TextThin-14 Gray centered>
            {getSubtitle(props)}
          </Text>
        </View>
      </View>
    </CardView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: rem(40),
        paddingBottom: rem(60),
    },
    containerWithRoundedCorners: {
        alignItems: 'center',
        paddingVertical: rem(24),
    },
    textContainer: {
        marginHorizontal: rem(55),
    },
    flex: {
        flex: 1,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    extraHorizontalPadding: {
        paddingHorizontal: '20%',
    },
    walkthroughCardStyles: {
        marginHorizontal: rem(0),
    },
}));
export default EmptyState;
